import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, Search } from "lucide-react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { ApiError } from "@/api/client";
import { documentsApi } from "@/api/documents";
import { documentRelationshipsApi } from "@/api/documentRelationships";
import {
  DIRECTIONAL_KINDS,
  KIND_LABELS,
} from "@/api/relationshipLabels";
import type { DocumentRelationshipKind } from "@/types/documentRelationships";

type Mode = "bulk" | "single";

interface Props {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  /** Required in single mode — the document the link starts from. */
  seedDocumentId?: string;
  /** Required in bulk mode — the selected document ids. */
  documentIds?: string[];
  folderId: string;
  titlesById: Record<string, string>;
  onLinked: () => void;
}

const SELECT_CLASS =
  "h-8 px-space-2 rounded-md border border-border-light text-small bg-white-pure";

function kindOptions(idCount: number): DocumentRelationshipKind[] {
  // Directional kinds only make sense for an exact pair; with >2 documents we
  // can only express symmetric grouping or a generic "Other".
  if (idCount === 2) return [...DIRECTIONAL_KINDS, "GroupedWith", "Other"];
  return ["GroupedWith", "Other"];
}

export function LinkRelationshipModal({
  open,
  onClose,
  mode,
  seedDocumentId,
  documentIds,
  folderId,
  titlesById,
  onLinked,
}: Props) {
  const [kind, setKind] = useState<DocumentRelationshipKind>("GroupedWith");
  // Single mode: the picked second document.
  const [search, setSearch] = useState("");
  const [pickedId, setPickedId] = useState<string | null>(null);
  // Directional order: when true the displayed first id is the "From".
  const [swapped, setSwapped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKind("GroupedWith");
      setSearch("");
      setPickedId(null);
      setSwapped(false);
      setError(null);
    }
  }, [open]);

  const pickerQuery = useQuery({
    queryKey: ["documents", "by-folder", folderId, search],
    queryFn: () =>
      documentsApi.list({
        folderId,
        q: search || undefined,
        pageSize: 50,
      }),
    enabled: open && mode === "single",
  });

  const pickerDocs = useMemo(
    () =>
      (pickerQuery.data?.data ?? []).filter((d) => d.id !== seedDocumentId),
    [pickerQuery.data, seedDocumentId],
  );

  // The two ids participating in the link, in display order.
  const pairIds: string[] = useMemo(() => {
    if (mode === "single") {
      return seedDocumentId && pickedId ? [seedDocumentId, pickedId] : [];
    }
    return documentIds ?? [];
  }, [mode, seedDocumentId, pickedId, documentIds]);

  const idCount = pairIds.length;
  const options = kindOptions(mode === "single" ? (idCount === 2 ? 2 : 0) : idCount);
  const isDirectional = DIRECTIONAL_KINDS.includes(kind);
  const showDirection = isDirectional && idCount === 2;

  // Final POST order. Directional kinds encode From->To via array order; the
  // first id is "From". Symmetric kinds are order-independent so we send a
  // stable sorted order.
  const orderedIds: string[] = useMemo(() => {
    if (idCount !== 2 || !isDirectional) return [...pairIds].sort();
    const [a, b] = pairIds;
    return swapped ? [b, a] : [a, b];
  }, [pairIds, idCount, isDirectional, swapped]);

  const title = (id: string) => titlesById[id] ?? id;

  const create = useMutation({
    mutationFn: () => documentRelationshipsApi.create(orderedIds, kind),
    onSuccess: () => {
      onLinked();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not create the link.",
      );
    },
  });

  const canSubmit = orderedIds.length === 2 || (orderedIds.length >= 2 && !isDirectional);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Link documents"
      size="md"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!canSubmit}
          >
            Link
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        {mode === "single" && (
          <>
            <FormField label="Search">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filename or title…"
                  className="pl-space-5"
                />
              </div>
            </FormField>

            <div className="border border-border-light rounded-md max-h-[280px] overflow-y-auto">
              {pickerQuery.isLoading ? (
                <div className="p-space-4 text-small text-gray-mid italic">Loading…</div>
              ) : pickerDocs.length === 0 ? (
                <div className="p-space-4 text-small text-gray-mid italic">
                  No other documents in this folder.
                </div>
              ) : (
                <ul className="m-0 p-0 list-none divide-y divide-border-light">
                  {pickerDocs.map((d) => (
                    <li key={d.id}>
                      <label className="flex items-center gap-space-3 p-space-3 hover:bg-white-warm cursor-pointer">
                        <Input
                          type="radio"
                          name="link-target"
                          className="!w-4 !h-4 !p-0"
                          checked={pickedId === d.id}
                          onChange={() => setPickedId(d.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-space-2">
                            <span className="font-semibold text-small text-onyx truncate">
                              {d.title}
                            </span>
                            <Badge status="info">{d.category}</Badge>
                          </div>
                          <p className="text-caption text-gray-mid m-0 mt-space-1 truncate">
                            {d.fileName}
                          </p>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <FormField label="Relationship">
          <select
            aria-label="Relationship type"
            className={SELECT_CLASS}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as DocumentRelationshipKind);
              setSwapped(false);
            }}
          >
            {options.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </FormField>

        {showDirection && (
          <div className="flex items-center gap-space-2 text-small text-onyx">
            <span>
              From <span className="font-semibold">{title(orderedIds[0])}</span> → To{" "}
              <span className="font-semibold">{title(orderedIds[1])}</span>
            </span>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setSwapped((s) => !s)}
              aria-label="Swap direction"
            >
              <ArrowLeftRight size={14} aria-hidden /> Swap
            </Button>
          </div>
        )}

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
